import moment from 'moment';
import { useMemo } from 'react';
import { usePerformanceDetailsQuery } from '../api/usePerformanceDetailsQuery';
import { usePerformanceQuery } from '../api/usePerformanceQuery';
import { usePositionsDetailsQuery } from '../api/usePositionsDetailsQuery';
import { useGroupedTransactionsQuery } from '../api/useGroupedTransactionsQuery';
import { TabSlug } from '../types';
import { usePeriod } from './usePeriod';
import { usePositionsDate } from './usePositionsDate';
import { useRouteParams } from './useRouteParams';
import { useTransactionFilters } from './useTransactionFilters';
import { useProjectedIncomeQuery } from '../api/useProjectedIncomeQuery';
import { useIsEmbeddedMobile } from '../../_hooks/useIsEmbeddedMobile';
import { useTransactionsQuery } from '../api/useTransactionsQuery';
import { useFeatureFlag } from '../../_hooks/useFeatureFlag';
import { Feature } from '../../CommonSelectors/CommonArea.types';

export const useUpdatedAt = () => {
  const { accountId, clientId, groupId, tabSlug } = useRouteParams();

  const isEmbeddedMobile = useIsEmbeddedMobile();

  const period = usePeriod();

  const positionsDate = usePositionsDate();

  const transactionFilters = useTransactionFilters();

  const showActivityDetails = useFeatureFlag(Feature.ActivityDetails);

  const useTransactions =
    isEmbeddedMobile || !showActivityDetails ? useTransactionsQuery : useGroupedTransactionsQuery;

  const { dataUpdatedAt: performanceUpdatedAt } = usePerformanceQuery(
    {
      clientId,
      period: period.value,
      startDate: period.dates[0],
      endDate: period.dates[1],
      accountId,
      groupId,
    },
    {
      enabled: tabSlug === TabSlug.Summary,
    },
  );

  const { dataUpdatedAt: performanceDetailsUpdatedAt } = usePerformanceDetailsQuery(
    {
      clientId,
      period: period.value,
      startDate: period.dates[0],
      endDate: period.dates[1],
      accountId,
      groupId,
    },
    {
      enabled: tabSlug === TabSlug.Performance,
    },
  );

  const { dataUpdatedAt: positionsUpdatedAt } = usePositionsDetailsQuery(
    {
      clientId,
      date: positionsDate,
      accountId,
      groupId,
    },
    {
      enabled: tabSlug === TabSlug.Allocation,
    },
  );

  const { dataUpdatedAt: transactionsUpdatedAt } = useTransactions(
    {
      clientId,
      accountId,
      groupId,
      ...transactionFilters,
      period: transactionFilters.period.value,
      startDate: transactionFilters.period.dates[0],
      endDate: transactionFilters.period.dates[1],
    },
    {
      enabled: tabSlug === TabSlug.Activity,
    },
  );

  const { dataUpdatedAt: projectedIncomeUpdatedAt } = useProjectedIncomeQuery(
    {
      accountId,
      groupId,
      period: period.value,
      startDate: period.dates[0],
      endDate: period.dates[1],
    },
    {
      enabled: tabSlug === TabSlug.Income,
    },
  );

  const updatedAt = useMemo(() => {
    switch (tabSlug) {
      case TabSlug.Summary:
        return performanceUpdatedAt;
      case TabSlug.Performance:
        return performanceDetailsUpdatedAt;
      case TabSlug.Allocation:
        return positionsUpdatedAt;
      case TabSlug.Activity:
        return transactionsUpdatedAt;
      case TabSlug.Income:
        return projectedIncomeUpdatedAt;
      default:
        return Date.now();
    }
  }, [
    tabSlug,
    performanceUpdatedAt,
    performanceDetailsUpdatedAt,
    positionsUpdatedAt,
    transactionsUpdatedAt,
    projectedIncomeUpdatedAt,
  ]);

  return moment(updatedAt && updatedAt > 0 ? updatedAt : undefined).format('MM/DD/YYYY [at] LT');
};
